import { useEffect } from 'react'
import { AlertCircle } from 'lucide-react'
import { useInfraVnets } from '@/hooks/useInfra'
import { lookupErrorMessage } from '@/services/infraLive'
import type { InfraProduct, InfraVnet, NetworkChoice } from '@/types/infrastructure'
import page from '@/pages/NewDeploymentPage.module.css'
import styles from './Infrastructure.module.css'
import { LookupSelect } from './LookupSelect'

interface NetworkSelectProps {
  product: InfraProduct
  environmentId: string
  regionId: string
  resourceGroupId: string
  value: NetworkChoice
  onChange: (value: NetworkChoice) => void
  /** Free IPs the subnet must have. Subnets with fewer are listed but cannot be chosen. Defaults to 1. */
  requiredIps?: number
  /** Heading used in messages and labels, for example "RDS server network". */
  label?: string
  idPrefix: string
  /** Applied once, when nothing is chosen yet and this network exists here with enough room. */
  defaultValue?: NetworkChoice | null
}

const toChoice = (vnet: InfraVnet, subnetId: string): NetworkChoice => {
  const subnet = vnet.subnets.find((item) => item.id === subnetId)
  return {
    vnet: { id: vnet.id, name: vnet.name },
    subnet: subnet ? { id: subnet.id, name: subnet.name, prefix: subnet.prefix, freeIps: subnet.freeIps } : null,
  }
}

/**
 * VNet then Subnet, from the existing networks of the chosen resource group. The subnet list is filtered by the chosen
 * VNet. A VNet that does not exist, or a subnet without room, is reported as an error: nothing is ever created or assumed.
 */
export function NetworkSelect({
  product,
  environmentId,
  regionId,
  resourceGroupId,
  value,
  onChange,
  requiredIps = 1,
  label = 'Network',
  idPrefix,
  defaultValue,
}: NetworkSelectProps) {
  const vnets = useInfraVnets(product, environmentId, regionId, resourceGroupId)
  const list = vnets.data ?? []
  const chosenVnet = list.find((item) => item.id === value.vnet?.id)
  const subnets = chosenVnet?.subnets ?? []
  const roomy = subnets.filter((subnet) => subnet.freeIps >= requiredIps)
  const loaded = !!vnets.data
  const noVnets = loaded && list.length === 0
  const noRoom = !!chosenVnet && subnets.length > 0 && roomy.length === 0
  const tooSmall = value.subnet !== null && value.subnet.freeIps < requiredIps
  const servers = `${requiredIps} server${requiredIps === 1 ? '' : 's'}`

  // Start from a suggested network (for example the one already chosen for the main server) when it fits here.
  useEffect(() => {
    if (value.vnet || !defaultValue?.vnet || !vnets.data) return
    const vnet = vnets.data.find((item) => item.id === defaultValue.vnet?.id)
    const subnet = vnet?.subnets.find((item) => item.id === defaultValue.subnet?.id)
    if (vnet && subnet && subnet.freeIps >= requiredIps) onChange(toChoice(vnet, subnet.id))
  }, [value.vnet, defaultValue, vnets.data, requiredIps, onChange])

  return (
    <div className={page.formGrid}>
      <LookupSelect
        id={`${idPrefix}-vnet`}
        label={`${label}: VNet`}
        required
        value={value.vnet?.id ?? ''}
        options={list.map((item) => ({ value: item.id, label: `${item.name}${item.addressSpace ? ` · ${item.addressSpace}` : ''}` }))}
        onChange={(id) => {
          const vnet = list.find((item) => item.id === id)
          onChange(vnet ? { vnet: { id: vnet.id, name: vnet.name }, subnet: null } : { vnet: null, subnet: null })
        }}
        waitingFor={resourceGroupId ? undefined : 'a resource group'}
        isLoading={vnets.isLoading}
        isError={vnets.isError}
        errorMessage={lookupErrorMessage(vnets.error)}
        onRetry={() => void vnets.refetch()}
        emptyMessage="No existing VNet in this resource group."
        hint="Existing VNets only."
      />
      <LookupSelect
        id={`${idPrefix}-subnet`}
        label={`${label}: Subnet`}
        required
        value={value.subnet?.id ?? ''}
        options={subnets.map((item) => ({
          value: item.id,
          label: `${item.name}${item.prefix ? ` · ${item.prefix}` : ''} · ${item.freeIps} free IP${item.freeIps === 1 ? '' : 's'}${item.freeIps < requiredIps ? ' (not enough space)' : ''}`,
          disabled: item.freeIps < requiredIps,
        }))}
        onChange={(id) => chosenVnet && onChange(toChoice(chosenVnet, id))}
        waitingFor={value.vnet ? undefined : 'a VNet'}
        emptyMessage="This VNet has no subnet to deploy into."
      />
      {noVnets && (
        <div role="alert" className={styles.alert} style={{ gridColumn: '1 / -1' }}>
          <span>
            <AlertCircle size={13} aria-hidden="true" style={{ marginRight: 6, verticalAlign: '-2px' }} />
            No existing VNet is available in {resourceGroupId}. Choose another resource group or region, or ask the network team to create one.
          </span>
        </div>
      )}
      {noRoom && (
        <div role="alert" className={styles.alert} style={{ gridColumn: '1 / -1' }}>
          <span>
            <AlertCircle size={13} aria-hidden="true" style={{ marginRight: 6, verticalAlign: '-2px' }} />
            No subnet in {chosenVnet?.name} has space for {servers}. Choose another VNet.
          </span>
        </div>
      )}
      {tooSmall && !noRoom && (
        <div role="alert" className={styles.alert} style={{ gridColumn: '1 / -1' }}>
          <span>
            <AlertCircle size={13} aria-hidden="true" style={{ marginRight: 6, verticalAlign: '-2px' }} />
            {value.subnet?.name} has {value.subnet?.freeIps} free IP{value.subnet?.freeIps === 1 ? '' : 's'}, not enough for {servers}. Choose another subnet.
          </span>
        </div>
      )}
    </div>
  )
}
